import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ProjectMapPage.css';
import {
  MapPin, RefreshCw, Search, X, XCircle, ChevronLeft, ChevronRight,
  Plus, Minus, Crosshair, Navigation, Maximize, Minimize, HardHat, ExternalLink,
  Loader, AlertTriangle, Info, Briefcase, Clock,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Pane } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';
import { useSiteContext } from '../../SiteContext';
import { Page } from '../../interfaces/PagesInterface';
import config from '../../configLoader';
import ProjectsAPI from '../../api/projects';
import {
  ProjectMapPin, ProjectMapSupervisor, ProjectMapCrewMember,
} from '../../interfaces/Project';
import { can } from '../../util/adminPermissions';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM } from '../../components/projectMapLeaflet';
import FloatMenuButton, { FloatMenuOption } from '../../components/v2/FloatMenuButton';
import { formatElapsed, formatPunchTime, initials, escapeHtml } from './crewUtil';

// ── Constants (design handoff: docs/featureBriefs/design_handoff_project_map) ──

/**
 * Location pin palette. Real locations carry no color field, so colors are
 * assigned client-side, cyclically by SiteContext location order (stable across
 * renders). First three are the handoff palette (LA/IE/SD); the rest extend it
 * with harmonious hues from the shared v2 semantic accents.
 */
const LOCATION_PALETTE = [
  '#2f6fed', '#0e7c86', '#7a5cf0', '#b1499c', '#d8852f', '#1f9d6b', '#b3471c', '#6b7686',
];
const ACCENT = '#2f6fed';
const CREW_GREEN = '#1f9d6b';

const coordKey = (p: ProjectMapPin) => `${p.latitude.toFixed(5)},${p.longitude.toFixed(5)}`;
const pinSupers = (p: ProjectMapPin): number[] => p.supervisorIDs ?? [];
const crewName = (c: ProjectMapCrewMember) => `${c.name.first} ${c.name.last}`.replace(/\s+/g, ' ').trim();
const supFullName = (s: ProjectMapSupervisor) => `${s.name.first} ${s.name.last}`.replace(/\s+/g, ' ').trim();

/** Crew name/ID match (same predicate as the Timesheet Review board filter bar):
 *  "First Last", "Last, First", or the employee ID. `t` must already be lowercased. */
const crewMatches = (c: ProjectMapCrewMember, t: string) => (
  `${c.name.first} ${c.name.last}`.toLowerCase().includes(t)
  || `${c.name.last}, ${c.name.first}`.toLowerCase().includes(t)
  || (c.altEmployeeID ?? '').toLowerCase().includes(t)
);

/** Max rows per suggestion group so the dropdown never runs off the card. */
const MAX_CREW_SUGGESTIONS = 8;
const MAX_PIN_SUGGESTIONS = 5;
/** Zoom used when flying to a picked employee / their site. */
const FOCUS_ZOOM = 16;

// ── Popup content components ─────────────────────────────────────────────────

interface SinglePopupProps {
  pin: ProjectMapPin;
  color: string;
  locName: string | null;
  supervisorNames: string[];
  crewList: ProjectMapCrewMember[];
  crewLayerOn: boolean;
  canOpenProjects: boolean;
  onOpenProject: (pin: ProjectMapPin) => void;
  nowMs: number;
  /** When drilled from a stacked mini-list: total co-located count for the back button. */
  backCount?: number;
  onBack?: () => void;
}

const SinglePopupContent: React.FC<SinglePopupProps> = ({
  pin, color, locName, supervisorNames, crewList, crewLayerOn,
  canOpenProjects, onOpenProject, nowMs, backCount, onBack,
}) => {
  const addressLine = [
    pin.address,
    [pin.city, pin.state].filter(Boolean).join(', '),
    pin.zip,
  ].filter(Boolean).join(', ');

  return (
    <div className="pmp">
      <div className="pmp-accent" style={{ background: color }} />
      <div className="pmp-in">
        {backCount != null && onBack && (
          <button type="button" className="pmp-back" onClick={onBack}>
            <ChevronLeft className="ic" />All {backCount} at this site
          </button>
        )}
        {locName && (
          <div className="pmp-loc" style={{ color }}>
            <span className="dot" style={{ background: color }} />{locName}
          </div>
        )}
        <h4>{pin.name}</h4>
        <span className="pmp-id">{pin.altProjectID}</span>
        <div className="pmp-addr">
          <MapPin className="ic" />
          <div>
            {addressLine
              ? <b>{addressLine}</b>
              : <span className="none">No street address on file</span>}
            {pin.crossStreets && <div className="cross">Cross streets: {pin.crossStreets}</div>}
          </div>
        </div>
        {supervisorNames.length > 0 && (
          <div className="pmp-meta">
            {supervisorNames.map((n) => (
              <span key={n} className="pmp-tag"><HardHat className="ic" />{n}</span>
            ))}
          </div>
        )}
        {crewLayerOn && crewList.length > 0 && (
          <div className="pmp-crew">
            <div className="cr-head">
              <span className="dot" />On site now<span className="ct">{crewList.length}</span>
            </div>
            {crewList.map((c) => {
              const name = crewName(c);
              return (
                <div className="cr" key={c.employeeID}>
                  <div className="av">{initials(name)}</div>
                  <div>
                    <div className="nm">{name}</div>
                    <div className="ti">{c.jobTitle || '—'}</div>
                  </div>
                  <div className="since">
                    <b>{formatElapsed(c.clockInDateTime, nowMs)}</b>
                    since {formatPunchTime(c.clockInDateTime, c.clockInDateTimeOffset)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {canOpenProjects && (
          <button type="button" className="pmp-open" onClick={() => onOpenProject(pin)}>
            <ExternalLink className="ic" />Open in Projects
          </button>
        )}
      </div>
    </div>
  );
};

interface GroupPopupProps extends Omit<SinglePopupProps, 'pin' | 'color' | 'locName' | 'supervisorNames' | 'crewList' | 'backCount' | 'onBack'> {
  list: ProjectMapPin[];
  colorFor: (p: ProjectMapPin) => string;
  locNameFor: (p: ProjectMapPin) => string | null;
  supervisorNamesFor: (p: ProjectMapPin) => string[];
  crewListFor: (p: ProjectMapPin) => ProjectMapCrewMember[];
}

/** Single-project popup, or (for stacked coordinates) the mini-list with drill-down. */
const GroupPopupContent: React.FC<GroupPopupProps> = ({
  list, colorFor, locNameFor, supervisorNamesFor, crewListFor,
  crewLayerOn, canOpenProjects, onOpenProject, nowMs,
}) => {
  const [drillIdx, setDrillIdx] = useState<number | null>(null);

  const renderSingle = (pin: ProjectMapPin, backCount?: number) => (
    <SinglePopupContent
      pin={pin}
      color={colorFor(pin)}
      locName={locNameFor(pin)}
      supervisorNames={supervisorNamesFor(pin)}
      crewList={crewListFor(pin)}
      crewLayerOn={crewLayerOn}
      canOpenProjects={canOpenProjects}
      onOpenProject={onOpenProject}
      nowMs={nowMs}
      backCount={backCount}
      onBack={backCount != null ? () => setDrillIdx(null) : undefined}
    />
  );

  if (list.length === 1) return renderSingle(list[0]);
  if (drillIdx != null && list[drillIdx]) return renderSingle(list[drillIdx], list.length);

  const first = list[0];
  const color = colorFor(first);
  const locName = locNameFor(first);
  const site = [first.address, first.city].filter(Boolean).join(', ') || 'This site';

  return (
    <div className="pmp">
      <div className="pmp-accent" style={{ background: color }} />
      <div className="pmp-in">
        <div className="pmp-stack-head">
          {locName && (
            <div className="pmp-loc" style={{ color }}>
              <span className="dot" style={{ background: color }} />{locName}
            </div>
          )}
          <h4>{list.length} projects at this location</h4>
          <span className="pmp-id site">{site}</span>
        </div>
        <div className="pmp-stacklist">
          {list.map((p, i) => {
            const crewN = crewLayerOn ? crewListFor(p).length : 0;
            return (
              <button type="button" className="pmp-stack-row" key={p.projectID} onClick={() => setDrillIdx(i)}>
                <div>
                  <div className="sn">{p.name}</div>
                  <div className="si">{p.altProjectID}</div>
                </div>
                {crewN > 0 && <span className="screw"><span className="dot" />{crewN}</span>}
                <span className="chev"><ChevronRight className="ic" /></span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CrewPopupContent: React.FC<{
  member: ProjectMapCrewMember;
  projectName: string | null;
  nowMs: number;
}> = ({ member, projectName, nowMs }) => {
  const name = crewName(member);
  return (
    <div className="pmp">
      <div className="pmp-accent" style={{ background: CREW_GREEN }} />
      <div className="pmp-in">
        <div className="pmp-loc crewloc">
          <span className="dot" style={{ background: CREW_GREEN }} />
          {member.status === 'meal' ? 'On meal break' : 'Clocked in'}
        </div>
        <h4>{name}</h4>
        <span className="pmp-id">
          {member.altEmployeeID}{member.jobTitle ? ` · ${member.jobTitle}` : ''}
        </span>
        <div className="pmp-meta">
          <span className="pmp-tag"><Briefcase className="ic" />{projectName ?? '—'}</span>
          <span className="pmp-tag">
            <Clock className="ic" />
            {formatElapsed(member.clockInDateTime, nowMs)} · since {formatPunchTime(member.clockInDateTime, member.clockInDateTimeOffset)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const ProjectMapPage: React.FC = () => {
  const { userRole, locations, setPage, setProjectIntent } = useSiteContext();

  // Crew data is gated server-side; check the permission up front and never
  // even call the endpoint without it. A 403 at runtime also hides the layer.
  const crewAllowed = can(userRole, 'viewTimesheets');
  const canOpenProjects = !!(config.showProjectsPage && userRole.viewProjectsPage);

  const [pins, setPins] = useState<ProjectMapPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [supervisors, setSupervisors] = useState<ProjectMapSupervisor[]>([]);
  const [crew, setCrew] = useState<ProjectMapCrewMember[]>([]);
  const [crewAvailable, setCrewAvailable] = useState(false);

  const [search, setSearch] = useState('');
  const [locFilter, setLocFilter] = useState<string | null>(null);
  /** Selected superintendents (empty = all). A pin matches if ANY of them is on it. */
  const [supFilter, setSupFilter] = useState<number[]>([]);
  const [crewOnly, setCrewOnly] = useState(false);
  /** Live Crew layer visibility toggle (defaults on for anyone with access). */
  const [showCrew, setShowCrew] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [locating, setLocating] = useState(false);
  /** Search typeahead: open state, highlighted row, and the picked employee (if any). */
  const [suggOpen, setSuggOpen] = useState(false);
  const [suggIndex, setSuggIndex] = useState(-1);
  /** Picked from the typeahead — kept visible even under Location/Super filters. */
  const [focusedCrewID, setFocusedCrewID] = useState<number | null>(null);
  /** Bumped on every pick so re-picking the same person re-opens their popup. */
  const [focusNonce, setFocusNonce] = useState(0);
  /** Set when a picked member's punch carries no coordinates and has no site to fall back to. */
  const [noFocusCoords, setNoFocusCoords] = useState<number | null>(null);
  /** 30s re-render tick so "time on site" labels stay current (Home crew cadence). */
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const [map, setMap] = useState<L.Map | null>(null);
  /** Bumped on every popupopen so stacked popups re-open on their mini-list. */
  const [popupNonce, setPopupNonce] = useState(0);

  const reqRef = useRef(0);
  const fitPendingRef = useRef(true);
  const locateMarkerRef = useRef<L.CircleMarker | null>(null);
  /** Live Leaflet marker handles so a typeahead pick can open the right popup. */
  const crewMarkerRefs = useRef(new Map<number, L.Marker>());
  const pinMarkerRefs = useRef(new Map<string, L.Marker>());
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  const applyCrewFailure = useCallback((err: unknown) => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 403) {
      // No crew access for this caller — silently hide all crew UI.
      setCrewAvailable(false);
      setCrew([]);
    }
    // Other failures: keep whatever crew data we already had.
  }, []);

  // ── Data loading (Refresh re-runs pins + supervisors + crew) ────────────────
  const load = useCallback(async () => {
    const reqID = ++reqRef.current;
    setLoading(true);
    setLoadError(false);

    ProjectsAPI.getMapSupervisors()
      .then((res) => { if (reqID === reqRef.current) setSupervisors(res.supervisors ?? []); })
      .catch(() => { /* non-fatal — the Super filter simply stays hidden */ });

    if (crewAllowed) {
      ProjectsAPI.getMapCrew()
        .then((res) => {
          if (reqID !== reqRef.current) return;
          setCrew(res.crew ?? []);
          setCrewAvailable(true);
          setNowMs(Date.now());
        })
        .catch((err) => { if (reqID === reqRef.current) applyCrewFailure(err); });
    }

    try {
      const data = await ProjectsAPI.getMapPins();
      if (reqID !== reqRef.current) return;
      setPins(data);
      fitPendingRef.current = true;
    } catch {
      if (reqID === reqRef.current) setLoadError(true);
    } finally {
      if (reqID === reqRef.current) setLoading(false);
    }
  }, [crewAllowed, applyCrewFailure]);

  useEffect(() => { load(); }, [load]);

  // Silent 60s crew poll while mounted (matches the Home Crew Live Status
  // cadence); skipped while the tab is hidden.
  useEffect(() => {
    if (!crewAllowed) return;
    const timer = window.setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await ProjectsAPI.getMapCrew();
        setCrew(res.crew ?? []);
        setCrewAvailable(true);
        setNowMs(Date.now());
      } catch (err) {
        applyCrewFailure(err);
      }
    }, 60000);
    return () => window.clearInterval(timer);
  }, [crewAllowed, applyCrewFailure]);

  // 30s tick for elapsed "time on site" text.
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  // ── Location colors / lookups ───────────────────────────────────────────────
  const hasLocationData = useMemo(
    () => locations.length > 0 && pins.some((p) => p.altProjectID2),
    [pins, locations]
  );

  const colorByAltLocationID = useMemo(() => {
    const m = new Map<string, string>();
    locations.forEach((l, i) => m.set(l.altLocationID, LOCATION_PALETTE[i % LOCATION_PALETTE.length]));
    return m;
  }, [locations]);

  const locationByAltID = useMemo(() => {
    const m = new Map<string, { name: string }>();
    locations.forEach((l) => m.set(l.altLocationID, l));
    return m;
  }, [locations]);

  const pinColor = useCallback((p: ProjectMapPin): string => {
    if (!hasLocationData) return ACCENT;
    return (p.altProjectID2 && colorByAltLocationID.get(p.altProjectID2)) || ACCENT;
  }, [hasLocationData, colorByAltLocationID]);

  const pinLocName = useCallback((p: ProjectMapPin): string | null => {
    if (!hasLocationData || !p.altProjectID2) return null;
    return locationByAltID.get(p.altProjectID2)?.name ?? null;
  }, [hasLocationData, locationByAltID]);

  /** Locations that actually appear in the pin payload (filter + legend rows). */
  const locationOptions = useMemo(() => {
    if (!hasLocationData) return [];
    const present = new Set(pins.map((p) => p.altProjectID2).filter(Boolean));
    return locations.filter((l) => present.has(l.altLocationID));
  }, [pins, locations, hasLocationData]);

  const supervisorByID = useMemo(() => {
    const m = new Map<number, ProjectMapSupervisor>();
    supervisors.forEach((s) => m.set(s.employeeID, s));
    return m;
  }, [supervisors]);

  const pinSupervisorNames = useCallback((p: ProjectMapPin): string[] => (
    pinSupers(p)
      .map((id) => supervisorByID.get(id))
      .filter((s): s is ProjectMapSupervisor => !!s)
      .map(supFullName)
  ), [supervisorByID]);

  // ── Crew lookups ────────────────────────────────────────────────────────────
  const crewLayerOn = crewAvailable && showCrew;

  const crewByProject = useMemo(() => {
    const m = new Map<number, ProjectMapCrewMember[]>();
    crew.forEach((c) => {
      if (c.projectID == null) return;
      const list = m.get(c.projectID);
      if (list) list.push(c);
      else m.set(c.projectID, [c]);
    });
    return m;
  }, [crew]);

  const crewCountFor = useCallback(
    (projectID: number) => crewByProject.get(projectID)?.length ?? 0,
    [crewByProject]
  );
  const crewListFor = useCallback(
    (p: ProjectMapPin) => crewByProject.get(p.projectID) ?? [],
    [crewByProject]
  );

  // ── Filters (client-side, live) ─────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const anyFilterActive = !!locFilter || supFilter.length > 0 || crewOnly || q !== '';

  const filteredPins = useMemo(() => pins.filter((p) => {
    if (locFilter && p.altProjectID2 !== locFilter) return false;
    if (supFilter.length > 0 && !pinSupers(p).some((id) => supFilter.includes(id))) return false;
    if (crewOnly && crewCountFor(p.projectID) === 0) return false;
    if (q) {
      const hay = [p.name, p.altProjectID, p.address, p.city, p.state, p.zip, p.crossStreets]
        .filter(Boolean).join(' ').toLowerCase();
      // A site also survives the text filter when someone matching is clocked in on it —
      // otherwise searching an employee name would hide the very pin they're standing on.
      const crewHit = crewLayerOn && crewListFor(p).some((c) => crewMatches(c, q));
      if (!hay.includes(q) && !crewHit) return false;
    }
    return true;
  }), [pins, locFilter, supFilter, crewOnly, q, crewCountFor, crewLayerOn, crewListFor]);

  const filteredProjectIDs = useMemo(
    () => new Set(filteredPins.map((p) => p.projectID)),
    [filteredPins]
  );

  /** Stacked coordinates render as one pin per coordinate group. */
  const groups = useMemo(() => {
    const m = new Map<string, ProjectMapPin[]>();
    filteredPins.forEach((p) => {
      const key = coordKey(p);
      const list = m.get(key);
      if (list) list.push(p);
      else m.set(key, [p]);
    });
    return Array.from(m.entries()).map(([key, list]) => ({ key, list }));
  }, [filteredPins]);

  // Crew member visibility under filters: members with a projectID follow their
  // (filtered) pin; siteless members show only when no pin-filters are active.
  /** True when the typed text names an actual clocked-in person — that flips the
   *  crew layer from "whoever is on the visible sites" to "the people you searched". */
  const crewQueryHit = useMemo(
    () => q !== '' && crewLayerOn && crew.some((c) => crewMatches(c, q)),
    [q, crewLayerOn, crew]
  );

  const visibleCrew = useMemo(() => {
    if (!crewLayerOn) return [];
    return crew.filter((c) => {
      // A typeahead pick always stays on the map, even under Location/Super filters.
      if (c.employeeID === focusedCrewID) return true;
      // Searching a name narrows the layer to the matches themselves — this also
      // covers siteless punches, which the pin-derived rule below would drop.
      if (crewQueryHit) return crewMatches(c, q);
      return c.projectID != null ? filteredProjectIDs.has(c.projectID) : !anyFilterActive;
    });
  }, [crew, crewLayerOn, filteredProjectIDs, anyFilterActive, q, crewQueryHit, focusedCrewID]);

  /** Markers need coordinates; null-gps members still count in chips/badges/popups. */
  const visibleCrewMarkers = useMemo(
    () => visibleCrew.filter((c) => c.gps != null),
    [visibleCrew]
  );

  const projectNameByID = useMemo(() => {
    const m = new Map<number, string>();
    pins.forEach((p) => m.set(p.projectID, p.name));
    return m;
  }, [pins]);

  // ── Map behaviors ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (map) map.attributionControl?.setPrefix('');
  }, [map]);

  const fitTo = useCallback((rows: { latitude: number; longitude: number }[]) => {
    if (!map || rows.length === 0) return;
    map.fitBounds(
      L.latLngBounds(rows.map((p) => [p.latitude, p.longitude] as [number, number])),
      { padding: [60, 60], maxZoom: 15 }
    );
  }, [map]);

  // Refit-on-change: handlers raise fitPendingRef, the effect fits once the
  // filtered set has recomputed. Live search typing intentionally never refits
  // (Enter does).
  useEffect(() => {
    if (fitPendingRef.current && map && filteredPins.length > 0) {
      fitPendingRef.current = false;
      fitTo(filteredPins);
    }
  }, [filteredPins, map, fitTo]);

  const refitSoon = () => { fitPendingRef.current = true; };

  /** flyTo that leaves the target ~75-80% down the viewport so its popup has room
   *  above it (same projection trick as the Home crew map). */
  const flyToWithPopupRoom = useCallback((lat: number, lng: number) => {
    if (!map) return;
    const pt = map.project(L.latLng(lat, lng), FOCUS_ZOOM);
    const containerH = map.getContainer().getBoundingClientRect().height || 500;
    pt.y -= containerH * 0.28;
    map.flyTo(map.unproject(pt, FOCUS_ZOOM), FOCUS_ZOOM, { duration: 0.55 });
  }, [map]);

  // ── Fullscreen (button or Esc to exit) ──────────────────────────────────────
  useEffect(() => {
    document.body.classList.toggle('pmv2-fs-lock', fullscreen);
    const t = window.setTimeout(() => {
      map?.invalidateSize();
      if (filteredPins.length) fitTo(filteredPins);
    }, 60);
    return () => {
      window.clearTimeout(t);
      document.body.classList.remove('pmv2-fs-lock');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen, map]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // ── Locate-me ───────────────────────────────────────────────────────────────
  const locate = () => {
    if (!map || !navigator.geolocation || locating) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const ll: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        if (locateMarkerRef.current) map.removeLayer(locateMarkerRef.current);
        locateMarkerRef.current = L.circleMarker(ll, {
          radius: 8, color: '#fff', weight: 3, fillColor: ACCENT, fillOpacity: 1,
        }).addTo(map);
        map.flyTo(ll, 14);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // ── Filter handlers ─────────────────────────────────────────────────────────
  const closeSuggest = useCallback(() => { setSuggOpen(false); setSuggIndex(-1); }, []);

  const clearFilters = () => {
    setLocFilter(null);
    setSupFilter([]);
    setCrewOnly(false);
    setSearch('');
    setFocusedCrewID(null);
    setNoFocusCoords(null);
    closeSuggest();
    refitSoon();
  };

  const handleOpenProject = useCallback((pin: ProjectMapPin) => {
    setProjectIntent({ projectID: pin.projectID, altProjectID: pin.altProjectID });
    setPage(Page.Projects);
  }, [setProjectIntent, setPage]);

  // ── Search suggestions ──────────────────────────────────────────────────────
  /** Matched over the *full* crew, not visibleCrew — someone hidden by a Location
   *  or Super filter must still be findable by name. */
  const crewSuggestions = useMemo(() => (
    (!q || !crewLayerOn) ? [] : crew.filter((c) => crewMatches(c, q)).slice(0, MAX_CREW_SUGGESTIONS)
  ), [q, crewLayerOn, crew]);

  const pinSuggestions = useMemo(() => (
    !q ? [] : filteredPins.filter((p) => {
      const hay = [p.name, p.altProjectID, p.address, p.city, p.state, p.zip, p.crossStreets]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, MAX_PIN_SUGGESTIONS)
  ), [q, filteredPins]);

  /** Flat list backing keyboard navigation across both groups. */
  const suggRows = useMemo(() => ([
    ...crewSuggestions.map((c) => ({ kind: 'crew' as const, member: c })),
    ...pinSuggestions.map((p) => ({ kind: 'pin' as const, pin: p })),
  ]), [crewSuggestions, pinSuggestions]);

  const suggVisible = suggOpen && suggRows.length > 0;

  const pickCrew = useCallback((c: ProjectMapCrewMember) => {
    closeSuggest();
    setShowCrew(true);
    setFocusedCrewID(c.employeeID);
    setNoFocusCoords(null);
    setFocusNonce((n) => n + 1);
    // The pending refit would fight the flyTo below.
    fitPendingRef.current = false;

    if (c.gps) {
      flyToWithPopupRoom(c.gps.latitude, c.gps.longitude);
      return; // popup opens from the focus effect once the marker has mounted
    }
    // No punch coordinates — fall back to the site they're clocked in on.
    const pin = c.projectID != null ? pins.find((p) => p.projectID === c.projectID) : undefined;
    if (pin) {
      flyToWithPopupRoom(pin.latitude, pin.longitude);
      // Pins live inside the cluster group; they only become real layers once
      // clustering switches off (zoom 16), so open after the fly settles.
      map?.once('moveend', () => pinMarkerRefs.current.get(coordKey(pin))?.openPopup());
      return;
    }
    setNoFocusCoords(c.employeeID);
  }, [closeSuggest, flyToWithPopupRoom, pins, map]);

  const pickPin = useCallback((p: ProjectMapPin) => {
    closeSuggest();
    fitPendingRef.current = false;
    flyToWithPopupRoom(p.latitude, p.longitude);
    map?.once('moveend', () => pinMarkerRefs.current.get(coordKey(p))?.openPopup());
  }, [closeSuggest, flyToWithPopupRoom, map]);

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { closeSuggest(); return; }
    if (!suggVisible) {
      if (e.key === 'Enter' && filteredPins.length) fitTo(filteredPins);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggIndex((i) => (i + 1) % suggRows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggIndex((i) => (i <= 0 ? suggRows.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = suggRows[suggIndex];
      if (row) {
        if (row.kind === 'crew') pickCrew(row.member);
        else pickPin(row.pin);
      } else if (filteredPins.length) {
        fitTo(filteredPins);
      }
    }
  };

  // Close the typeahead on an outside click (Leaflet swallows some events, so
  // mousedown on the document is the reliable hook).
  useEffect(() => {
    if (!suggVisible) return;
    const onDown = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) closeSuggest();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [suggVisible, closeSuggest]);

  // ── Icons ───────────────────────────────────────────────────────────────────
  const clusterIcon = useCallback((cluster: { getChildCount: () => number }) => {
    const n = cluster.getChildCount();
    const tier = n < 12 ? 's1' : n < 60 ? 's2' : 's3';
    return L.divIcon({
      className: `pm-cluster ${tier}`,
      iconSize: null as unknown as undefined,
      html: `<div class="cl"><span class="ring"></span>${n}</div>`,
    });
  }, []);

  const groupEntries = useMemo(() => groups.map(({ key, list }) => {
    const first = list[0];
    const color = pinColor(first);
    const crewN = crewLayerOn ? list.reduce((n, p) => n + crewCountFor(p.projectID), 0) : 0;
    const stacked = list.length > 1;
    const badge = crewN > 0 ? `<span class="crew-badge">${crewN}</span>` : '';
    const inner = stacked ? `<span class="cnt">${list.length}</span>` : '';
    const icon = L.divIcon({
      className: '',
      iconSize: [30, 38],
      iconAnchor: [15, 30],
      popupAnchor: [0, -28],
      html: `<div class="pm-pin${stacked ? ' stacked' : ''}"><div class="pin-body" style="background:${color}">${inner}</div>${badge}</div>`,
    });
    return { key, list, first, icon };
  }), [groups, pinColor, crewLayerOn, crewCountFor]);

  const crewMarkerEntries = useMemo(() => visibleCrewMarkers.map((c) => ({
    member: c,
    icon: L.divIcon({
      className: '',
      iconSize: [34, 42],
      iconAnchor: [17, 38],
      popupAnchor: [0, -34],
      html: `<div class="pm-crew"><span class="pulse"></span><span class="cpin"><span class="cav">${escapeHtml(initials(crewName(c)))}</span></span></div>`,
    }),
  })), [visibleCrewMarkers]);

  // Open the picked employee's popup once their marker has actually mounted (the
  // pick itself only sets state; the marker appears on the following render).
  // crewMarkerEntries is in the deps purely as the "markers have re-rendered"
  // signal — a pick changes visibleCrew, so this fires after the ref is attached.
  useEffect(() => {
    if (focusNonce === 0 || focusedCrewID == null) return;
    void crewMarkerEntries;
    crewMarkerRefs.current.get(focusedCrewID)?.openPopup();
  }, [focusNonce, focusedCrewID, crewMarkerEntries]);

  // ── Filter option lists / labels ────────────────────────────────────────────
  const locMenuOptions: FloatMenuOption[] = useMemo(() => ([
    { value: '', label: 'All locations' },
    ...locationOptions.map((l) => ({
      value: l.altLocationID,
      label: l.name,
      color: colorByAltLocationID.get(l.altLocationID),
      count: pins.filter((p) => p.altProjectID2 === l.altLocationID).length,
    })),
  ]), [locationOptions, colorByAltLocationID, pins]);

  const supMenuOptions: FloatMenuOption[] = useMemo(() => ([
    { value: '', label: 'All supervisors' },
    ...supervisors.map((s) => ({
      value: String(s.employeeID),
      label: supFullName(s),
      count: pins.filter((p) => pinSupers(p).includes(s.employeeID)).length,
    })),
  ]), [supervisors, pins]);

  const locValueLabel = locFilter
    ? (locationByAltID.get(locFilter)?.name ?? 'All locations')
    : 'All locations';
  const supValueLabel = (() => {
    if (supFilter.length === 0) return 'All supervisors';
    if (supFilter.length > 1) return `${supFilter.length} supervisors`;
    const s = supervisorByID.get(supFilter[0]);
    return s ? supFullName(s) : '1 supervisor';
  })();

  /** "All" clears; any other row toggles that superintendent in or out. */
  const toggleSup = (v: string) => {
    if (v === '') setSupFilter([]);
    else {
      const id = Number(v);
      setSupFilter((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }
    refitSoon();
  };

  const showLocationFilter = hasLocationData && locationOptions.length > 0;
  const showSupFilter = supervisors.length > 0;

  /** Per-location counts of the *filtered* set for the legend. */
  const filteredCountByLoc = useMemo(() => {
    const m = new Map<string, number>();
    filteredPins.forEach((p) => {
      if (!p.altProjectID2) return;
      m.set(p.altProjectID2, (m.get(p.altProjectID2) ?? 0) + 1);
    });
    return m;
  }, [filteredPins]);

  const showLegend = !loading && !loadError && (showLocationFilter || crewLayerOn);
  // A name search can land on someone with no site pin — don't paint "no projects"
  // over a perfectly valid crew marker.
  const noResults = !loading && !loadError && filteredPins.length === 0 && visibleCrew.length === 0;

  const noFocusCoordsName = useMemo(() => {
    if (noFocusCoords == null) return null;
    const c = crew.find((m) => m.employeeID === noFocusCoords);
    return c ? crewName(c) : null;
  }, [noFocusCoords, crew]);

  const subtitle = crewAvailable
    ? "Every active project with GPS coordinates, plotted and clustered across the company. Filter by location or superintendent, search a project or an employee by name or ID, and see who's clocked in right now."
    : 'Every active project with GPS coordinates, plotted and clustered across the company. Filter by location or superintendent, or search by name, ID, or address.';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="pmv2">
      {/* breadcrumb */}
      <div className="crumb">
        <b>Projects</b>
        <ChevronRight className="cx" size={15} />
        <span className="here">Project Map</span>
      </div>

      {/* page header */}
      <div className="page-head">
        <span className="ph-ic"><MapPin /></span>
        <div>
          <h1>Project Map</h1>
          <div className="ph-sub">{subtitle}</div>
        </div>
        <div className="ph-right">
          <button type="button" className="btn sm" onClick={() => setFullscreen(true)}>
            <Maximize />Fullscreen
          </button>
          <button type="button" className="btn sm" onClick={load} disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} />Refresh
          </button>
        </div>
      </div>

      {/* filter bar + map: the wrapper becomes the fullscreen overlay so the
          filters stay usable while the map fills the viewport. It must stay
          mounted in both modes — remounting MapContainer would reset the map. */}
      <div className={`mapzone${fullscreen ? ' fs' : ''}`}>
      {/* filter bar */}
      <div className="filterbar">
        <div className={`searchwrap${search ? ' has-val' : ''}`} ref={searchWrapRef}>
          <Search className="ic si" />
          <input
            type="text"
            placeholder={crewLayerOn ? 'Search projects or clocked-in crew…' : 'Search name, ID, address, city…'}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSuggOpen(true);
              setSuggIndex(-1);
              // Editing the query invalidates any previous pick.
              setFocusedCrewID(null);
              setNoFocusCoords(null);
            }}
            // onClick as well as onFocus: after Escape the input keeps focus, so
            // clicking back into it must be able to re-open the panel.
            onFocus={() => { if (search.trim()) setSuggOpen(true); }}
            onClick={() => { if (search.trim()) setSuggOpen(true); }}
            onKeyDown={onSearchKeyDown}
            autoComplete="off"
          />
          <button
            type="button"
            className="sx"
            title="Clear search"
            onClick={() => {
              setSearch('');
              setFocusedCrewID(null);
              setNoFocusCoords(null);
              closeSuggest();
              refitSoon();
            }}
          >
            <X className="ic" />
          </button>

          {suggVisible && (
            <div className="sugg">
              {crewSuggestions.length > 0 && (
                <>
                  <div className="sg-head"><span className="dot" />Live crew<span className="ct">{crewSuggestions.length}</span></div>
                  {crewSuggestions.map((c, i) => {
                    const name = crewName(c);
                    const site = c.projectID != null ? projectNameByID.get(c.projectID) : null;
                    return (
                      <button
                        type="button"
                        key={`sc-${c.employeeID}`}
                        className={`sg-row crew${suggIndex === i ? ' active' : ''}`}
                        onMouseEnter={() => setSuggIndex(i)}
                        onClick={() => pickCrew(c)}
                      >
                        <span className="av">{initials(name)}</span>
                        <span className="txt">
                          <span className="nm">{name}</span>
                          <span className="sub">
                            {[c.altEmployeeID, c.jobTitle, site].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span className={`el${c.status === 'meal' ? ' meal' : ''}`}>
                          {c.status === 'meal' ? 'meal' : 'on site'}
                          <b>{formatElapsed(c.clockInDateTime, nowMs)}</b>
                        </span>
                      </button>
                    );
                  })}
                </>
              )}

              {pinSuggestions.length > 0 && (
                <>
                  <div className="sg-head"><MapPin className="ic" />Projects<span className="ct">{pinSuggestions.length}</span></div>
                  {pinSuggestions.map((p, i) => {
                    const idx = crewSuggestions.length + i;
                    const addr = [p.address, p.city].filter(Boolean).join(', ');
                    return (
                      <button
                        type="button"
                        key={`sp-${p.projectID}`}
                        className={`sg-row${suggIndex === idx ? ' active' : ''}`}
                        onMouseEnter={() => setSuggIndex(idx)}
                        onClick={() => pickPin(p)}
                      >
                        <span className="av pin" style={{ background: pinColor(p) }}>
                          <MapPin className="ic" />
                        </span>
                        <span className="txt">
                          <span className="nm">{p.name}</span>
                          <span className="sub">{[p.altProjectID, addr].filter(Boolean).join(' · ')}</span>
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>

        {showLocationFilter && (
          <FloatMenuButton
            label="Location"
            valueLabel={locValueLabel}
            isPlaceholder={!locFilter}
            isActive={!!locFilter}
            options={locMenuOptions}
            selectedValue={locFilter ?? ''}
            onSelect={(v) => { setLocFilter(v === '' ? null : v); refitSoon(); }}
          />
        )}

        {showSupFilter && (
          <FloatMenuButton
            label="Super"
            valueLabel={supValueLabel}
            isPlaceholder={supFilter.length === 0}
            isActive={supFilter.length > 0}
            options={supMenuOptions}
            multiple
            selectedValues={supFilter.map(String)}
            onSelect={toggleSup}
            searchable
          />
        )}

        {crewAvailable && (
          <button
            type="button"
            className={`crew-toggle${showCrew ? ' on' : ''}`}
            onClick={() => {
              const next = !showCrew;
              setShowCrew(next);
              if (!next) {
                setFocusedCrewID(null);
                setNoFocusCoords(null);
                if (crewOnly) { setCrewOnly(false); refitSoon(); }
              }
            }}
          >
            <span className="sw" />Live crew
          </button>
        )}

        {crewLayerOn && (
          <button
            type="button"
            className={`crew-toggle${crewOnly ? ' on' : ''}`}
            onClick={() => { setCrewOnly((v) => !v); refitSoon(); }}
          >
            <span className="sw" />Crew on site
          </button>
        )}

        {anyFilterActive && (
          <button type="button" className="clear-btn" onClick={clearFilters}>
            <XCircle className="ic" />Clear
          </button>
        )}

        {fullscreen && (
          <button
            type="button"
            className="fs-exit-btn"
            title="Exit fullscreen (Esc)"
            onClick={() => setFullscreen(false)}
          >
            <Minimize className="ic" />Exit Full Screen
          </button>
        )}

        {/* selected superintendents, visible without opening the Super menu */}
        {supFilter.length > 0 && (
          <div className="sup-chips">
            <span className="sc-lab">Supers</span>
            {supFilter.map((id) => {
              const s = supervisorByID.get(id);
              const name = s ? supFullName(s) : `#${id}`;
              return (
                <span className="sc-chip" key={id}>
                  <span className="nm" title={name}>{name}</span>
                  <button
                    type="button"
                    className="x"
                    aria-label={`Remove ${name}`}
                    onClick={() => toggleSup(String(id))}
                  >
                    <X className="ic" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* picked someone whose punch carried no coordinates and no site to fall back to */}
        {noFocusCoordsName && (
          <div className="pm-note">
            <AlertTriangle className="ic" />
            <span>{noFocusCoordsName} is clocked in, but this punch has no location to map.</span>
            <button type="button" className="nx" onClick={() => setNoFocusCoords(null)}>
              <X className="ic" />
            </button>
          </div>
        )}
      </div>

      {/* map card */}
      <div className="mapcard">
        <MapContainer
          center={[37.5, -96]}
          zoom={4}
          zoomControl={false}
          scrollWheelZoom
          worldCopyJump
          ref={setMap}
        >
          <TileLayer
            url={MAP_TILE_URL}
            attribution={MAP_TILE_ATTRIBUTION}
            maxZoom={MAP_TILE_MAX_ZOOM}
            referrerPolicy="strict-origin-when-cross-origin"
          />
          <MarkerClusterGroup
            chunkedLoading
            disableClusteringAtZoom={16}
            showCoverageOnHover={false}
            maxClusterRadius={52}
            iconCreateFunction={clusterIcon}
          >
            {groupEntries.map(({ key, list, first, icon }) => (
              <Marker
                key={key}
                position={[first.latitude, first.longitude]}
                icon={icon}
                ref={(m) => {
                  if (m) pinMarkerRefs.current.set(key, m);
                  else pinMarkerRefs.current.delete(key);
                }}
                eventHandlers={{ popupopen: () => setPopupNonce((n) => n + 1) }}
              >
                {/* closeOnClick=false: the drill-down render swaps the popup DOM mid-click,
                    so Leaflet's map-click close would misread the click as outside the
                    popup and close it. Close via the X, Esc, or opening another popup. */}
                {/* maxHeight bounds the content (long stack lists / crew lists scroll
                    internally) so Leaflet's autoPan never overshoots the map card. */}
                <Popup className="pm-popup" maxWidth={320} minWidth={288} maxHeight={380} autoPanPadding={[30, 30]} closeOnClick={false}>
                  <GroupPopupContent
                    key={`${key}:${popupNonce}`}
                    list={list}
                    colorFor={pinColor}
                    locNameFor={pinLocName}
                    supervisorNamesFor={pinSupervisorNames}
                    crewListFor={crewListFor}
                    crewLayerOn={crewLayerOn}
                    canOpenProjects={canOpenProjects}
                    onOpenProject={handleOpenProject}
                    nowMs={nowMs}
                  />
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>

          {/* TAS-405: crew markers live in their own pane BELOW Leaflet's markerPane
              (z 600), so every job pin, stacked pin and cluster bubble — all in the
              markerPane via MarkerClusterGroup — draws on top of the crew at every
              zoom (clustered or not). A zIndexOffset can't do this: it only orders
              markers within one pane, and the old +1000 put crew over the jobs.
              590 stays above overlay/shadow panes (400/500); popups (700) are
              unaffected. */}
          <Pane name="pmCrewPane" style={{ zIndex: 590 }}>
          {crewMarkerEntries.map(({ member, icon }) => (
            <Marker
              key={`crew-${member.employeeID}`}
              position={[member.gps!.latitude, member.gps!.longitude]}
              icon={icon}
              ref={(m) => {
                if (m) crewMarkerRefs.current.set(member.employeeID, m);
                else crewMarkerRefs.current.delete(member.employeeID);
              }}
            >
              <Popup className="pm-popup" maxWidth={300} minWidth={240}>
                <CrewPopupContent
                  member={member}
                  projectName={member.projectID != null ? (projectNameByID.get(member.projectID) ?? null) : null}
                  nowMs={nowMs}
                />
              </Popup>
            </Marker>
          ))}
          </Pane>
        </MapContainer>

        {/* control cluster (top-left) */}
        <div className="map-controls">
          <div className="cgroup">
            <button type="button" className="mbtn" title="Zoom in" onClick={() => map?.zoomIn()}>
              <Plus className="ic" />
            </button>
            <button type="button" className="mbtn" title="Zoom out" onClick={() => map?.zoomOut()}>
              <Minus className="ic" />
            </button>
          </div>
          <div className="cgroup">
            <button
              type="button"
              className={`mbtn${locating ? ' locating' : ''}`}
              title="Find my location"
              onClick={locate}
            >
              <Crosshair className="ic" />
            </button>
          </div>
          <div className="cgroup">
            <button
              type="button"
              className="mbtn"
              title="Fit all pins"
              onClick={() => { if (filteredPins.length) fitTo(filteredPins); }}
            >
              <Navigation className="ic" />
            </button>
          </div>
          <div className="cgroup">
            {fullscreen ? (
              <button
                type="button"
                className="mbtn wide"
                title="Exit fullscreen (Esc)"
                onClick={() => setFullscreen(false)}
              >
                <Minimize className="ic" />Exit
              </button>
            ) : (
              <button
                type="button"
                className="mbtn"
                title="Fullscreen"
                onClick={() => setFullscreen(true)}
              >
                <Maximize className="ic" />
              </button>
            )}
          </div>
        </div>

        {/* floating result count (top-center) */}
        {!loading && !loadError && (
          <div className="map-count">
            <MapPin className="ic" />
            <span>
              <b>{filteredPins.length}</b> of {pins.length} projects
              {anyFilterActive && <span className="filt"> · filtered</span>}
            </span>
          </div>
        )}

        {/* crew summary (top-right) */}
        {!loading && !loadError && crewLayerOn && (
          <div className="map-count crew-sum">
            <span className="dot" />
            {crewQueryHit
              ? <>{visibleCrew.length} of {crew.length} clocked in</>
              : <>{visibleCrew.length} clocked in</>}
          </div>
        )}

        {/* legend (bottom-left) */}
        {showLegend && (
          <div className="map-legend">
            {showLocationFilter && (
              <>
                <div className="lg-title">Locations</div>
                {locationOptions.map((l) => (
                  <div className="lg" key={l.locationID}>
                    <span className="sw" style={{ background: colorByAltLocationID.get(l.altLocationID) }} />
                    {l.name}
                    <span className="cnt">{filteredCountByLoc.get(l.altLocationID) ?? 0}</span>
                  </div>
                ))}
              </>
            )}
            {crewLayerOn && (
              <>
                {showLocationFilter ? <div className="lg-div" /> : <div className="lg-title">Live crew</div>}
                <div className="lg crew">
                  <span className="sw" />Crew clocked in<span className="cnt">{visibleCrew.length}</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* no-results overlay */}
        {noResults && (
          <div className="map-state show">
            <div className="st-ic"><Search className="ic" /></div>
            {pins.length === 0 ? (
              <>
                <h3>No projects to map</h3>
                <p>No active projects have GPS coordinates yet.</p>
              </>
            ) : (
              <>
                <h3>{q ? 'Nothing matches your search' : 'No projects match these filters'}</h3>
                {q ? (
                  <p>
                    Nothing matches <code>{search.trim()}</code>
                    {crewLayerOn
                      ? ' — no project and nobody clocked in right now. Try a different name, employee ID, or address.'
                      : '. Try a different name, ID, or address — or clear the filters.'}
                  </p>
                ) : (
                  <p>No active projects fall under the current filters. Broaden or clear them to see the full map.</p>
                )}
                <div className="st-act">
                  <button type="button" className="btn" onClick={clearFilters}>
                    <XCircle />Clear filters
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* loading overlay */}
        {loading && (
          <div className="map-load show">
            <div className="spin-wrap"><Loader className="ic" /></div>
            <p>Loading project map…</p>
          </div>
        )}

        {/* error overlay */}
        {!loading && loadError && (
          <div className="map-err show">
            <div className="st-ic"><AlertTriangle className="ic" /></div>
            <h3>Couldn't load the project map</h3>
            <p>Failed to load project map pins. Check your connection and try again.</p>
            <div className="st-act">
              <button type="button" className="btn primary" onClick={load}>
                <RefreshCw />Retry
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* footer hint */}
      <div className="map-foot">
        <Info className="ic" />
        <span>
          Click a pin for project details · click a cluster to zoom in
          {crewLayerOn ? ' · green markers are employees clocked in right now' : ''}
        </span>
      </div>
    </div>
  );
};

export default ProjectMapPage;
